import { useCallback, useEffect, useRef, useState } from 'react';
import type { FacesheetAnchorRef } from './types';

interface UseFacesheetOutlineNavigationOptions {
  defaultActiveAnchorId?: string;
  shouldObserveAnchor?: (anchorId: string) => boolean;
}

// Fixed distance (px) below the top of the scroll container where a section
// becomes active. A fixed offset keeps the activation line near the top
// regardless of viewport height.
const ACTIVATION_OFFSET_PX = 120;

export const useFacesheetOutlineNavigation = ({
  defaultActiveAnchorId,
  shouldObserveAnchor = () => true,
}: UseFacesheetOutlineNavigationOptions = {}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const anchorElementsRef = useRef<Map<string, HTMLElement>>(new Map());
  const [activeAnchorId, setActiveAnchorId] = useState(defaultActiveAnchorId);

  const updateActiveAnchor = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const activationLine = containerRect.top + ACTIVATION_OFFSET_PX;

    let activeAnchor: string | undefined;
    let activeTop = -Infinity;
    let topmostAnchor: string | undefined;
    let topmostTop = Infinity;
    let lastAnchor: string | undefined;
    let lastTop = -Infinity;

    anchorElementsRef.current.forEach((element, anchorId) => {
      if (!shouldObserveAnchor(anchorId)) return;

      const { top } = element.getBoundingClientRect();

      if (top < topmostTop) {
        topmostTop = top;
        topmostAnchor = anchorId;
      }

      if (top > lastTop) {
        lastTop = top;
        lastAnchor = anchorId;
      }

      if (top <= activationLine && top > activeTop) {
        activeTop = top;
        activeAnchor = anchorId;
      }
    });

    // When scrolled to the bottom, short trailing sections may never reach the
    // activation line, so force-activate the last section instead.
    const isScrollable = container.scrollHeight > container.clientHeight;
    const isAtBottom =
      isScrollable &&
      container.scrollTop + container.clientHeight >=
        container.scrollHeight - 1;

    const nextAnchor = isAtBottom
      ? (lastAnchor ?? activeAnchor ?? topmostAnchor)
      : (activeAnchor ?? topmostAnchor);

    if (nextAnchor) {
      setActiveAnchorId(nextAnchor);
    }
  }, [shouldObserveAnchor]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    updateActiveAnchor();

    container.addEventListener('scroll', updateActiveAnchor, { passive: true });
    window.addEventListener('resize', updateActiveAnchor);

    return () => {
      container.removeEventListener('scroll', updateActiveAnchor);
      window.removeEventListener('resize', updateActiveAnchor);
    };
  }, [updateActiveAnchor]);

  const getAnchorRef = useCallback<FacesheetAnchorRef>(
    (anchorId) => {
      return (element) => {
        if (!element) {
          anchorElementsRef.current.delete(anchorId);
          return;
        }

        anchorElementsRef.current.set(anchorId, element);
        updateActiveAnchor();
      };
    },
    [updateActiveAnchor],
  );

  const scrollToAnchor = useCallback((anchorId: string) => {
    const element = anchorElementsRef.current.get(anchorId);
    if (!element) return;

    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveAnchorId(anchorId);
  }, []);

  return {
    scrollContainerRef,
    getAnchorRef,
    activeAnchorId,
    scrollToAnchor,
  };
};
